<template>
  <div class="flex flex-col h-full p-4">
    <div
      v-if="isHydratingLobby"
      class="absolute inset-0 z-40 bg-background/70 backdrop-blur-sm flex items-center justify-center"
    >
      <div class="text-center">
        <div class="w-12 h-12 border-4 border-t-primary border-surface rounded-full animate-spin mx-auto mb-4" />
        <p class="text-text-secondary text-lg">Connecting to room…</p>
      </div>
    </div>

    <!-- Back button (mobile only) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-5 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="handleBack"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>
    <!-- Header with room info -->
    <div class="relative bg-surface rounded-2xl p-1 mb-3 sm:my-4">
      <!-- Back button (desktop) -->
      <div v-if="!isMobileDevice" class="z-10 absolute">
        <IconButton
          variant="text"
          size="sm"
          aria-label="Go back"
          @click="handleBack"
        >
          <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
        </IconButton>
      </div>
      <div class="flex justify-center items-start">
        <ClassroomRoomCode :code="roomCode" />
      </div>
    </div>

    <div class="flex flex-1 gap-4 min-h-0">
      <!-- Left: Participants list -->
      <div class="w-80 bg-surface rounded-2xl p-4 flex flex-col">
        <h2 class="text-lg font-semibold text-text-primary mb-2">
          Participants ({{ participants.length }})
        </h2>
        <ClassroomParticipantsList :participants="participants" class="flex-1 overflow-auto" />
      </div>

      <!-- Right: Song selection (host) or waiting (student) -->
      <div class="flex-1 bg-surface rounded-2xl p-4 flex flex-col min-w-0">
        <template v-if="isHost">
          <h2 class="text-lg font-semibold text-text-primary mb-2">Select a Song</h2>

          <ClassroomLobbyPieceTable
            :selected-piece-id="selectedSong?.musicalPieceId"
            @select="handlePieceSelect"
          />

          <!-- Control bar for host -->
          <div class="pt-4 border-t border-text-secondary/20">
            <ClassroomControlBar
              :selected-song="selectedSong?.name"
              :disabled="!canStartSession"
              @start="handleStart"
            />
          </div>
        </template>

        <template v-else-if="room">
          <!-- Student waiting state -->
          <div class="flex-1 flex flex-col items-center justify-center text-center">
            <p class="text-xl font-semibold text-text-primary">
              {{ selectedSong ? selectedSong.name : 'No song selected yet' }}
            </p>
            <p class="mt-2 text-text-secondary">
              {{ studentWaitingMessage }}
            </p>
          </div>
        </template>
        <template v-else>
          <div class="flex-1 flex items-center justify-center">
            <div class="w-8 h-8 border-4 border-t-primary border-surface rounded-full animate-spin" />
          </div>
        </template>
      </div>
    </div>

    <ClassroomExitConfirmDialog
      v-model:is-visible="showExitConfirm"
      :message="exitConfirmMessage"
      @confirm="confirmExit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onActivated, onMounted, onUnmounted, watch, ref } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useMultiplayerRoom } from '@/composables/useMultiplayerRoom';
import { CLASSROOM_LOBBY_ROUTE, CLASSROOM_ROUTE_NAMES } from '@/config/multiplayer';
import { useToast } from '@/composables/useToast';
import { goBackByRoute } from '@/composables/useRouteBack';
import { canStartClassroomSession } from '@/utils/classroomSongState';
import ClassroomRoomCode from '@/components/classroom/ClassroomRoomCode.vue';
import ClassroomParticipantsList from '@/components/classroom/ClassroomParticipantsList.vue';
import ClassroomControlBar from '@/components/classroom/ClassroomControlBar.vue';
import ClassroomLobbyPieceTable from '@/components/ClassroomLobbyPieceTable.vue';
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import { useScreenWakeLock } from '@/composables/useScreenWakeLock';
import IconButton from '@/components/IconButton.vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';
import ClassroomExitConfirmDialog from '@/components/ClassroomExitConfirmDialog.vue';
import { useClassroomExitConfirm } from '@/composables/useClassroomExitConfirm';
import type { Id } from '../../convex/_generated/dataModel';

const route = useRoute();
const router = useRouter();
const { createToast } = useToast();
const { isMobileDevice } = useDeviceDetection();
// Waiting for the teacher is dead time, which is exactly when a tablet
// auto-locks — and a locked screen stops the heartbeat that holds the seat.
// Keep the lobby awake for the same reason the game view stays awake.
useScreenWakeLock();

const pendingMusicalPieceId = ref<Id<'musicalPieces'> | null>(null);
let latestSelectionRequestId = 0;

const {
  room,
  participants,
  selectedSong,
  isHost,
  roomCode,
  connectionStatus,
  isConnected,
  selectMusicalPiece,
  startSession,
  reconnectToRoom,
  leaveRoom,
  onSessionStart,
  closeRoom,
} = useMultiplayerRoom();

const isHydratingLobby = computed(() =>
  !room.value || connectionStatus.value === 'connecting'
);

const {
  showExitConfirm,
  guardExit,
  skipNextConfirm,
  confirmExit,
  reset: resetExitConfirm,
} = useClassroomExitConfirm(router);

const exitConfirmMessage = computed(() =>
  isHost.value
    ? 'Leaving will close the classroom for everyone. Are you sure you want to exit?'
    : 'Are you sure you want to leave the classroom?'
);

const canStartSession = computed(() =>
  canStartClassroomSession({
    room: room.value,
    selectedSong: selectedSong.value,
    pendingMusicalPieceId: pendingMusicalPieceId.value,
  })
);

// Joining mid-run is allowed, so say why nothing is happening yet.
const studentWaitingMessage = computed(() =>
  room.value?.status === 'playing'
    ? "Lesson in progress — you'll join in at the next break"
    : 'Waiting for Host to Start!'
);

// Get room code from URL
const urlCode = String(route.params.code ?? '').toUpperCase();

// This view is `<keep-alive>`d, so re-entering the same room URL restores the
// cached instance and `onMounted` never runs again — but by then the room may
// have been torn down by the leave guard below. Both entry points therefore run
// the same connect check. (`onActivated` does not fire on the first mount, so
// the two never double up.)
const connectIfNeeded = async () => {
  // Defensive reset: this view is cached, and nothing should carry an armed
  // confirmation bypass into a freshly (re)connected room.
  resetExitConfirm();
  if (!urlCode) {
    createToast({
      type: 'error',
      message: 'Invalid room code',
    });
    skipNextConfirm();
    router.push({ name: 'classroom' });
    return;
  }

  // `isConnected` and not just `room`: teardown leaves the last snapshot in
  // place, so a populated room can outlive the subscriptions behind it.
  const connectedRoomCode = room.value?.code?.toUpperCase();
  if (!room.value || connectedRoomCode !== urlCode || !isConnected.value) {
    try {
      await reconnectToRoom(urlCode);
    } catch (err) {
      createToast({
        type: 'error',
        message: 'Failed to connect to room',
      });
      skipNextConfirm();
      router.push({ name: 'classroom' });
    }
  }
};

onMounted(connectIfNeeded);
onActivated(connectIfNeeded);

const offSessionStart = onSessionStart(() => {
  // Navigate to game view when host starts the session
  router.push({ name: 'classroom-game', params: { code: urlCode } });
});

// If user enters lobby URL while room is already in-session, jump into game
// directly. The route check is what keeps this quiet while cached: deactivated,
// this view still watches the room, and both it and the game view would
// otherwise react to the same change.
watch(
  room,
  (nextRoom, prevRoom) => {
    if (route.name !== CLASSROOM_LOBBY_ROUTE) return;
    if (!nextRoom || nextRoom?.status === 'finished') {
      if (!prevRoom) return;
      if (!isHost.value) {
        createToast({
          type: 'info',
          message: 'Host left the game. Room has been closed.',
        });
      }
      skipNextConfirm();
      router.push({ name: 'classroom' });
      return;
    }

    // A run in progress holds a client here instead of dropping them into the
    // middle of a song — a late joiner, or one whose device slept through the
    // start, waits for the next break. Both kinds of break release them:
    // 'paused' mid-song, and 'ended' between runs, each landing them in the
    // game view with the piece loaded and ready for whatever the host does
    // next. 'waiting' means no run at all. The host is never held; they have to
    // reach their own controls after a reload.
    if (nextRoom.status === 'waiting') return;
    if (nextRoom.status === 'playing' && !isHost.value) return;
    router.push({ name: 'classroom-game', params: { code: urlCode } });
  },
  { immediate: true }
);

const handlePieceSelect = async (pieceId: Id<'musicalPieces'>, name: string) => {
  const requestId = ++latestSelectionRequestId;
  pendingMusicalPieceId.value = pieceId;
  try {
    await selectMusicalPiece(pieceId, name);
  } catch (error) {
    if (requestId !== latestSelectionRequestId) return;
    pendingMusicalPieceId.value = null;
    createToast({
      type: 'error',
      message: error instanceof Error ? error.message : 'Failed to select song',
    });
  }
};

watch(selectedSong, (song) => {
  if (song?.musicalPieceId === pendingMusicalPieceId.value) {
    pendingMusicalPieceId.value = null;
  }
});

const handleStart = async () => {
  if (!canStartSession.value) {
    createToast({
      type: 'error',
      message: pendingMusicalPieceId.value
        ? 'Waiting for the selected song to finish loading'
        : 'Select a song before starting the session',
    });
    return;
  }
  await startSession();
  // Navigation happens via onSessionStart event hook for all participants.
};

const handleBack = () => {
  goBackByRoute(router, route);
};

// Leaving the classroom entirely is what ends the session — for the host by
// closing the room, for a student by giving up their seat. Moving between the
// lobby and the game keeps both. A route guard rather than onUnmounted so it
// covers every exit (back button, browser back, a redirect from a watcher),
// and so the mutations are sent while the room state they need is still around.
onBeforeRouteLeave(async (to) => {
  if (CLASSROOM_ROUTE_NAMES.has(to.name as string)) return true;
  if (!guardExit(to)) return false;
  try {
    await (isHost.value ? closeRoom() : leaveRoom());
  } catch (error) {
    console.error('[Classroom] Failed to leave room:', error);
  }
  return true;
});

onUnmounted(() => {
  offSessionStart.off();
});
</script>
